import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * useIdleTimer
 * Tracks user activity and triggers logout after inactivity timeout.
 * Shows a warning before auto-logout.
 *
 * @param {Object} options
 * @param {number} options.timeout - Inactivity timeout in ms (default: 30 minutes)
 * @param {number} options.warningDuration - Warning period before logout in ms (default: 60 seconds)
 * @param {Function} options.onLogout - Function to call when auto-logout triggers
 * @param {boolean} options.enabled - Whether the timer is active (default: true)
 * @returns {{ showWarning: boolean, remainingSeconds: number, stayActive: Function }}
 */
export function useIdleTimer({
  timeout = 30 * 60 * 1000, // 30 minutes
  warningDuration = 60 * 1000, // 60 seconds warning
  onLogout,
  enabled = true,
}) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(Math.floor(warningDuration / 1000));

  const warningActiveRef = useRef(false); // tracks warning state without re-renders
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);
  const onLogoutRef = useRef(onLogout);

  // Keep logout ref updated
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  // Start/reset all timers
  const startTimers = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    warningActiveRef.current = false;
    setShowWarning(false);
    setRemainingSeconds(Math.floor(warningDuration / 1000));

    if (!enabled) return;

    // Warning timer — fires (timeout - warningDuration) ms after last activity
    warningRef.current = setTimeout(() => {
      warningActiveRef.current = true;
      setShowWarning(true);
      setRemainingSeconds(Math.floor(warningDuration / 1000));

      // Countdown every second
      countdownRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeout - warningDuration);

    // Logout timer — fires after full timeout
    timeoutRef.current = setTimeout(() => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      warningActiveRef.current = false;
      setShowWarning(false);
      if (onLogoutRef.current) onLogoutRef.current();
    }, timeout);
  }, [timeout, warningDuration, enabled]);

  // User explicitly clicks "Stay Logged In"
  const stayActive = useCallback(() => {
    startTimers();
  }, [startTimers]);

  // Track user activity — set up once, doesn't depend on showWarning state
  useEffect(() => {
    if (!enabled) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const handleActivity = () => {
      // If warning is showing, ignore activity — only explicit "Stay" button resets
      if (warningActiveRef.current) return;
      startTimers();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timers
    startTimers();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [enabled, startTimers]);

  return { showWarning, remainingSeconds, stayActive };
}
